import { useCallback, useEffect, useState } from "react";
import { notificationApi, type NotificationItem } from "./notification-api";

const POLL_MS = 30_000;

export interface UseNotificationsResult {
  items: NotificationItem[];
  unreadCount: number;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

export function useNotifications(enabled = true): UseNotificationsResult {
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;

    try {
      setError(null);
      const [list, count] = await Promise.all([
        notificationApi.list(1, 20),
        notificationApi.unreadCount(),
      ]);
      setItems(list.items);
      setUnreadCount(count.count);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Notifications indisponibles");
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  const markRead = useCallback(
    async (id: string) => {
      await notificationApi.markRead(id);
      await refresh();
    },
    [refresh],
  );

  const markAllRead = useCallback(async () => {
    await notificationApi.markAllRead();
    await refresh();
  }, [refresh]);

  useEffect(() => {
    void refresh();
    if (!enabled) return undefined;

    const timer = window.setInterval(() => {
      void refresh();
    }, POLL_MS);

    return () => window.clearInterval(timer);
  }, [enabled, refresh]);

  return {
    items,
    unreadCount,
    loading,
    error,
    refresh,
    markRead,
    markAllRead,
  };
}
